/**
 * Connections Page
 * Manage API connections (OAuth2, Bearer, etc.)
 * Includes duplicate and export functionality
 */

import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Link2,
  Play,
  Trash2,
  Edit,
  RefreshCw,
  Check,
  AlertCircle,
  Shield,
  Key,
  Copy,
  Download,
  Upload,
  FileJson,
} from 'lucide-react';
import { Layout } from '../components/layout';
import { Modal } from '../components/ui';
import { ConnectionForm } from '../components/forms';
import { connectionsApi } from '../api/client';
import { Connection } from '../types';

export function ConnectionsPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const {
    data: connections,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['connections'],
    queryFn: connectionsApi.list,
  });

  const filteredConnections = connections?.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.base_url.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const createMutation = useMutation({
    mutationFn: (data: Partial<Connection>) => connectionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
      setIsModalOpen(false);
      setEditingConnection(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      connectionsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
      setIsModalOpen(false);
      setEditingConnection(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => connectionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => connectionsApi.test(id),
    onMutate: (id) => {
      setTestingId(id);
      setTestResult(null);
    },
    onSuccess: (result) => {
      setTestResult(result);
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
    onError: (error: Error) => {
      setTestResult({ success: false, message: error.message });
    },
    onSettled: () => {
      setTestingId(null);
    },
  });

  const handleAdd = () => {
    setEditingConnection(null);
    setIsModalOpen(true);
  };

  const handleEdit = (connection: Connection) => {
    setEditingConnection(connection);
    setIsModalOpen(true);
  };

  const handleDelete = (connection: Connection) => {
    if (confirm(`Are you sure you want to delete the connection "${connection.name}"?`)) {
      deleteMutation.mutate(connection.id);
    }
  };

  // Duplicate/Copy a connection
  const handleDuplicate = (connection: Connection) => {
    // Create a copy of the connection without id and with modified name
    const duplicateData: Partial<Connection> = {
      name: `${connection.name} (Copy)`,
      description: connection.description,
      base_url: connection.base_url,
      tenant: connection.tenant,
      auth_type: connection.auth_type,
      token_url: connection.token_url,
      client_id: connection.client_id,
      oauth2_scope: connection.oauth2_scope,
      custom_headers: connection.custom_headers,
      is_active: false, // Set inactive by default for safety
    };

    createMutation.mutate(duplicateData);
  };

  // Export a single connection as JSON
  const handleExportConnection = (connection: Connection) => {
    const exportData = {
      name: connection.name,
      description: connection.description,
      base_url: connection.base_url,
      tenant: connection.tenant,
      auth_type: connection.auth_type,
      token_url: connection.token_url,
      client_id: connection.client_id,
      oauth2_scope: connection.oauth2_scope,
      custom_headers: connection.custom_headers,
      // Note: We don't export secrets for security reasons
      _exported_at: new Date().toISOString(),
      _note: 'Secrets (client_secret, access_token, etc.) are not exported for security reasons. Please enter them manually after import.',
    };

    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `connection_${connection.name.replace(/[^a-zA-Z0-9]/g, '_')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export all connections as JSON
  const handleExportAll = () => {
    if (!connections || connections.length === 0) {
      alert('No connections available for export');
      return;
    }

    const exportData = connections.map((connection) => ({
      name: connection.name,
      description: connection.description,
      base_url: connection.base_url,
      tenant: connection.tenant,
      auth_type: connection.auth_type,
      token_url: connection.token_url,
      client_id: connection.client_id,
      oauth2_scope: connection.oauth2_scope,
      custom_headers: connection.custom_headers,
    }));

    const exportWrapper = {
      _type: 'r4s_omnimens_connections',
      _version: '1.0',
      _exported_at: new Date().toISOString(),
      _count: exportData.length,
      _note: 'Secrets are not exported for security reasons.',
      connections: exportData,
    };

    const jsonStr = JSON.stringify(exportWrapper, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    link.download = `connections_export_${timestamp}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Import connections from JSON file
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportError(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      let connectionsToImport: Partial<Connection>[] = [];

      // Check if it's a single connection or multiple
      if (data._type === 'r4s_omnimens_connections' && Array.isArray(data.connections)) {
        connectionsToImport = data.connections;
      } else if (data.name && data.base_url) {
        // Single connection
        connectionsToImport = [data];
      } else {
        throw new Error('Invalid file format');
      }

      // Import each connection
      let successCount = 0;
      for (const conn of connectionsToImport) {
        try {
          await connectionsApi.create({
            ...conn,
            name: `${conn.name} (Import)`,
            is_active: false,
          });
          successCount++;
        } catch (err) {
          console.error('Failed to import connection:', conn.name, err);
        }
      }

      queryClient.invalidateQueries({ queryKey: ['connections'] });
      setTestResult({
        success: true,
        message: `${successCount} of ${connectionsToImport.length} connections successfully imported`,
      });
    } catch (err) {
      console.error('Import error:', err);
      setImportError(err instanceof Error ? err.message : 'Import failed');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Map form data to API fields
  const mapFormToApi = (formData: Record<string, unknown>): Record<string, unknown> => {
    const apiData: Record<string, unknown> = {
      name: formData.name,
      description: formData.description,
      base_url: formData.base_url,
      tenant: formData.tenant,
      auth_type: formData.auth_type,
      custom_headers: formData.custom_headers,
    };

    // Map OAuth2 fields
    if (formData.auth_type === 'oauth2') {
      apiData.token_url = formData.oauth2_token_url;
      apiData.client_id = formData.oauth2_client_id;
      apiData.oauth2_scope = formData.oauth2_scope;
      if (formData.oauth2_client_secret) {
        apiData.client_secret = formData.oauth2_client_secret;
      }
      if (formData.service_account_key) {
        apiData.service_account_key = formData.service_account_key;
      }
      if (formData.service_account_secret) {
        apiData.service_account_secret = formData.service_account_secret;
      }
    }

    // Map Bearer token
    if (formData.auth_type === 'bearer' && formData.bearer_token) {
      apiData.access_token = formData.bearer_token;
    }

    // Map Basic auth
    if (formData.auth_type === 'basic') {
      if (formData.basic_username) {
        apiData.client_id = formData.basic_username;
      }
      if (formData.basic_password) {
        apiData.access_token = formData.basic_password;
      }
    }

    return apiData;
  };

  const handleSubmit = (formData: Record<string, unknown>) => {
    const apiData = mapFormToApi(formData);

    if (editingConnection) {
      updateMutation.mutate({ id: editingConnection.id, data: apiData });
    } else {
      createMutation.mutate(apiData as Partial<Connection>);
    }
  };

  const handleTest = (connection: Connection) => {
    testMutation.mutate(connection.id);
  };

  const getAuthIcon = (authType: string) => {
    switch (authType) {
      case 'oauth2':
        return <Shield className="w-4 h-4 text-purple-600" />;
      case 'bearer':
        return <Key className="w-4 h-4 text-[#45bdd6]" />;
      default:
        return <Link2 className="w-4 h-4 text-gray-600" />;
    }
  };

  const getAuthLabel = (authType: string) => {
    switch (authType) {
      case 'oauth2':
        return 'OAuth2';
      case 'bearer':
        return 'Bearer Token';
      case 'basic':
        return 'Basic Auth';
      default:
        return authType;
    }
  };

  return (
    <Layout
      title="Connections"
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Connections
            </h1>
            <p className="text-gray-500 mt-1">
              Manage API connections and authentication
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Import Button */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImportFile}
              accept=".json"
              className="hidden"
            />
            <button
              onClick={handleImportClick}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title="Import connections"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>

            {/* Export All Button */}
            <button
              onClick={handleExportAll}
              disabled={!connections || connections.length === 0}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export all connections"
            >
              <Download className="w-4 h-4" />
              Export All
            </button>

            {/* Add Button */}
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Connection
            </button>
          </div>
        </div>

        {/* Test Result / Import Error Toast */}
        {(testResult || importError) && (
          <div
            className={`mb-4 p-4 rounded-lg flex items-center gap-3 ${
              importError
                ? 'bg-red-50 border border-red-200 text-red-800'
                : testResult?.success
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            {importError ? (
              <AlertCircle className="w-5 h-5 text-red-600" />
            ) : testResult?.success ? (
              <Check className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <span>{importError || testResult?.message}</span>
            <button
              onClick={() => {
                setTestResult(null);
                setImportError(null);
              }}
              className="ml-auto text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-200 rounded" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-1/2 mb-2" />
                    <div className="h-4 bg-gray-100 rounded w-3/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-600">
              Failed to load connections. Please try again.
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && filteredConnections?.length === 0 && (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
            <Link2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No connections configured
            </h3>
            <p className="text-gray-500 mb-4">
              Add your first API connection to start fetching data
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={handleImportClick}
                className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Upload className="w-5 h-5" />
                Import
              </button>
              <button
                onClick={handleAdd}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf]"
              >
                <Plus className="w-5 h-5" />
                Add Connection
              </button>
            </div>
          </div>
        )}

        {/* Connections Grid */}
        {!isLoading && !error && filteredConnections && filteredConnections.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredConnections.map((connection) => (
              <div
                key={connection.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded ${
                      connection.auth_type === 'oauth2' ? 'bg-purple-50' :
                      connection.auth_type === 'bearer' ? 'bg-blue-50' : 'bg-gray-50'
                    }`}>
                      {getAuthIcon(connection.auth_type)}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{connection.name}</h3>
                      <p className="text-xs text-gray-500">{getAuthLabel(connection.auth_type)}</p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                      connection.is_active
                        ? 'bg-green-50 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {connection.is_active ? (
                      <>
                        <Check className="w-3 h-3" />
                        Active
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-3 h-3" />
                        Inactive
                      </>
                    )}
                  </span>
                </div>

                {connection.description && (
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                    {connection.description}
                  </p>
                )}

                <div className="text-xs text-gray-400 mb-3 truncate">
                  {connection.base_url}
                </div>

                {connection.last_error && (
                  <div className="bg-red-50 border border-red-100 rounded p-2 mb-3">
                    <p className="text-xs text-red-600 truncate">{connection.last_error}</p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-400">
                    {connection.last_used_at
                      ? `Last used: ${new Date(connection.last_used_at).toLocaleDateString('en-GB')}`
                      : 'Never used'}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleTest(connection)}
                      disabled={testingId === connection.id}
                      className="p-1.5 text-gray-400 hover:text-[#45bdd6] hover:bg-blue-50 rounded disabled:opacity-50"
                      title="Test Connection"
                    >
                      {testingId === connection.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDuplicate(connection)}
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                      title="Duplicate"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleExportConnection(connection)}
                      className="p-1.5 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded"
                      title="Export as JSON"
                    >
                      <FileJson className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(connection)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(connection)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingConnection(null);
        }}
        title={editingConnection ? 'Edit Connection' : 'New Connection'}
        size="xl"
      >
        <ConnectionForm
          connection={editingConnection}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingConnection(null);
          }}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>
    </Layout>
  );
}
